import i18n from '../i18n';

/**
 * RMC2 display helpers, ported from the prototype's `utils/helpers.ts`.
 *
 * IMPORTANT — what did NOT come across.
 *
 * The prototype computed commission rates, the banking-day payout calendar and
 * membership expiry in the browser, because it had no backend. Those all now
 * live server-side (Rules 74/75/76) and the API returns the results. Duplicating
 * them here would create two implementations that WILL drift, and the one the
 * member screenshots is the one you are held to.
 *
 * So this file holds display-only concerns: dates, status labels, and the badge
 * tone map. If you find yourself about to compute money or a credit date here,
 * the answer is an API field.
 *
 * These read the i18n singleton directly rather than taking a `t` argument,
 * because they are called from table `render` callbacks and cell formatters
 * where threading a translator through every call site would be noise. Every
 * page that uses them calls `useTranslation()`, so a language change still
 * re-renders their output.
 */

/** The BCP-47 tag for date formatting in the active language. */
const dateLocale = () => (i18n.resolvedLanguage === 'tl' ? 'fil-PH' : 'en-PH');

/** "15 Jan 2026". Returns an em dash for null so tables never show "Invalid Date". */
export function formatDate(value) {
  if (!value) return '—';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  return d.toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "15 Jan 2026, 2:30 PM" — for audit trails and ticket threads. */
export function formatDateTime(value) {
  if (!value) return '—';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  return d.toLocaleString(dateLocale(), {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

/** "in 12 days" / "3 days ago" / "today" — for expiry chips. */
export function relativeDays(value) {
  if (!value) return '—';

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';

  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86_400_000);

  if (days === 0) return i18n.t('rmc2.when.today');
  if (days === 1) return i18n.t('rmc2.when.tomorrow');
  if (days === -1) return i18n.t('rmc2.when.yesterday');

  return days > 0
    ? i18n.t('rmc2.when.inDays', { count: days })
    : i18n.t('rmc2.when.daysAgo', { count: Math.abs(days) });
}

/**
 * Badge tone for every status string in the system.
 *
 * One map rather than a ternary at each call site: a new status is a new entry
 * here, and nothing renders an unstyled badge because it fell through an
 * if-chain someone forgot to extend.
 */
const STATUS_TONE = {
  // members
  Active: 'emerald',
  Inactive: 'slate',
  Expired: 'rose',

  // billing
  Unpaid: 'slate',
  'Payment Verification': 'amber',
  'Payment Verified': 'sky',
  Paid: 'emerald',
  'Returned for Resubmission': 'rose',

  // claims
  Pending: 'amber',
  Approved: 'sky',
  Rejected: 'rose',
  'Under Appeal': 'violet',

  // commissions + payouts
  Available: 'emerald',
  Requested: 'amber',
  Processing: 'amber',
  Released: 'sky',

  // tickets
  Open: 'amber',
  'In Progress': 'sky',
  Resolved: 'emerald',

  // ticket priority
  Normal: 'slate',
  Urgent: 'rose',

  // branches
  // (Active / Inactive already covered above)
};

/** The badge class for a status. Unknown statuses render neutral, never unstyled. */
export function statusTone(status) {
  return STATUS_TONE[status] ?? 'slate';
}

export function statusBadgeClass(status) {
  return `rmc2-badge rmc2-badge-${statusTone(status)}`;
}

/**
 * The four renewal warning thresholds (spec §8.1), for the expiry chip.
 * Mirrors the server's `RenewalService::THRESHOLDS` — display only; the server
 * decides who actually gets notified.
 */
export function expiryTone(daysRemaining) {
  if (daysRemaining === null || daysRemaining === undefined) return 'slate';
  if (daysRemaining <= 0) return 'rose';
  if (daysRemaining <= 3) return 'rose';
  if (daysRemaining <= 15) return 'amber';
  if (daysRemaining <= 30) return 'amber';
  return 'emerald';
}

/** The five member-facing statuses, for filter dropdowns. */
export const MEMBER_STATUSES = ['Active', 'Inactive', 'Expired'];

export const BILL_STATUSES = [
  'Unpaid', 'Payment Verification', 'Payment Verified', 'Paid', 'Returned for Resubmission',
];

export const CLAIM_STATUSES = ['Pending', 'Approved', 'Rejected', 'Under Appeal', 'Paid'];

export const PAYOUT_STATUSES = ['Pending', 'Processing', 'Released', 'Rejected'];

export const TICKET_STATUSES = ['Open', 'In Progress', 'Resolved'];

export const TICKET_PRIORITIES = ['Normal', 'Urgent'];

/**
 * What a member can be asking about, offered as a select rather than free text.
 *
 * `support_tickets.category` is a plain string server-side, so this list is a UI
 * convention, not a constraint — it exists so the queue can be filtered and
 * grouped instead of accumulating forty spellings of "billing". Mirrors the
 * modules a member actually deals with; `Other` is the escape hatch so nobody
 * abandons the form because their problem does not fit a box.
 */
export const TICKET_CATEGORIES = [
  'Billing',
  'Claims',
  'Network',
  'Payouts',
  'Account',
  'Other',
];

/** Level labels for the network tree and the commission ledger. */
export function levelLabel(level) {
  const n = Number(level);
  if (n === 0) return i18n.t('rmc2.level.general');
  if (n === 1) return i18n.t('rmc2.level.direct');
  return i18n.t('rmc2.level.n', { count: n });
}

/**
 * The display label for a status. The server's enum value stays the wire
 * format and the filter value; only what the user reads is translated, so a
 * language change can never alter what gets sent back.
 */
export function statusLabel(status) {
  if (!status) return '—';
  return i18n.t(`rmc2.status.${status}`, { defaultValue: status });
}
